import React, { useState, useEffect } from "react";
import {
  TextField,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Select,
  InputLabel,
  FormControl,
  Snackbar,
  Alert,
} from "@mui/material";
import dayjs from "dayjs";

import { useNavigate } from "react-router-dom";
import { getDecryptedAccessToken, getDecryptedData } from "../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";
import ApiServices from "../../../Utils/ApiServices/ApiServices";

const ProviderScheduleCreate = ({doctor}) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [slotDuration, setSlotDuration] = useState(60); 
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [availabilityMode, setAvailabilityMode] = useState("");
  const AccessToken = getDecryptedAccessToken();
  const LoginUserData = getDecryptedData();
  const providerId = doctor?.id || LoginUserData.id;
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const navigate = useNavigate();
  const weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  // Generate available slots based on duration
  useEffect(() => {
    generateSlots();
  }, [slotDuration]);

  const generateSlots = () => {
    const slots = [];
    let startTime = dayjs().hour(9).minute(0).second(0); // Set start time at 9:00 AM
  
    while (startTime.hour() < 18) { // Until 6:00 PM
      let endTime = startTime.add(slotDuration, "minute");
  
      if (endTime.hour() >= 18) break; // Stop when endTime exceeds 6:00 PM
  
      slots.push(`${startTime.format("HH:mm")}-${endTime.format("HH:mm")}`);
      startTime = endTime; // Move to next slot start
    }
  
    setAvailableSlots(slots);
  };
  

  // Handle slot selection with conflict validation
  const handleSlotSelection = (slot) => {
    if (selectedSlots.includes(slot)) {
      // Deselect slot and remove conflicts
      setSelectedSlots(selectedSlots.filter((s) => s !== slot));
    } else {
      // Validate conflicts
      const newSelected = [...selectedSlots, slot];
      const validSlots = validateConflicts(newSelected);
      setSelectedSlots(validSlots);
    }
  };

  const validateConflicts = (slots) => {
    return slots.filter((slot1) => {
      return !slots.some(
        (slot2) =>
          slot1 !== slot2 &&
          isConflict(slot1, slot2) // Check conflicts
      );
    });
  };

  const isConflict = (slot1, slot2) => {
    const [s1Start, s1End] = slot1.split("-").map((t) => dayjs(t, "HH:mm"));
    const [s2Start, s2End] = slot2.split("-").map((t) => dayjs(t, "HH:mm"));
    return s1End.isAfter(s2Start) && s1Start.isBefore(s2End);
  };

  // Handle form submission
  const handleSubmit = async () => {

      if (!startDate || !endDate || daysOfWeek.length === 0 || selectedSlots.length === 0 || !availabilityMode) {
  setSnackbar({ open: true, message: "Please fill all fields", severity: "error" });
  return;
}


    const payload = {
      providerId,
      startDate,
      endDate,
      daysOfWeek,
      slots: selectedSlots,
      type: "In Office",
      availability_mode: availabilityMode,
    };

    try {
      const response = await ApiServices.post("/apis/default/api/setAvailability", payload,
        {
            Authorization: `Bearer ${AccessToken}`,
      });
      setSnackbar({ open: true, message: "Schedule Created Successfully!", severity: "success" });
      resetForm();
      // setTimeout(() => navigate(-1), 1000);
    } catch (error) {
      console.error("Error:", error);
      setSnackbar({ open: true, message: "Failed to create schedule", severity: "error" });
    }
  };
  const resetForm = () => {
    setStartDate("");
    setEndDate("");
    setDaysOfWeek([]);
    setSelectedSlots([]);
     setAvailabilityMode("");
  };

  return (
    <div style={{ padding: 20 }}>
     

      <TextField
        label="Start Date"
        type="date"
        InputLabelProps={{ shrink: true }}
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        inputProps={{ min: dayjs().format("YYYY-MM-DD") }}
        fullWidth
        margin="normal"
      />

      <TextField
        label="End Date"
        type="date"
        InputLabelProps={{ shrink: true }}
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        inputProps={{ min: startDate }}
        fullWidth
        margin="normal"
      />

      <FormControl fullWidth margin="normal">
  <InputLabel>Slot Type</InputLabel>
  <Select
    value={availabilityMode}
    onChange={(e) => setAvailabilityMode(e.target.value)}
  >
    <MenuItem value="online">Online</MenuItem>
    <MenuItem value="offline">Offline</MenuItem>
    <MenuItem value="both">Both Online and Offline</MenuItem>
  </Select>
</FormControl>


      <FormGroup row>
        {weekDays.map((day) => (
          <FormControlLabel
            key={day}
            control={
              <Checkbox
                checked={daysOfWeek.includes(day)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setDaysOfWeek([...daysOfWeek, day]);
                  } else {
                    setDaysOfWeek(daysOfWeek.filter((d) => d !== day));
                  }
                }}
              />
            }
            label={day}
          />
        ))}
      </FormGroup>

      <FormControl fullWidth margin="normal">
        <InputLabel>Slot Duration</InputLabel>
        <Select
          value={slotDuration}
          onChange={(e) => setSlotDuration(e.target.value)}
        >
          <MenuItem value={30}>30 Minutes</MenuItem>
          <MenuItem value={45}>45 Minutes</MenuItem>
          <MenuItem value={60}>60 Minutes</MenuItem>
        </Select>
      </FormControl>

      <h3>Select Slots:</h3>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        {availableSlots.map((slot) => (
          <Button
            key={slot}
            variant={selectedSlots.includes(slot) ? "contained" : "outlined"}
            color={selectedSlots.includes(slot) ? "primary" : "secondary"}
            onClick={() => handleSlotSelection(slot)}
            disabled={
              selectedSlots.some((s) => isConflict(s, slot)) &&
              !selectedSlots.includes(slot)
            }
          >
            {slot}
          </Button>
        ))}
      </div>

      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleSubmit}
        style={{ marginTop: 20 }}
      >
        Submit Schedule
      </Button>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default ProviderScheduleCreate;
